import { useEffect, useState, useRef } from 'react';

interface UseTextOverflowProps {
  text: string;
  maxLines?: number;
}

export const useTextOverflow = <T extends HTMLElement>({ text, maxLines = 2 }: UseTextOverflowProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const elementRef = useRef<T>(null);

  useEffect(() => {
    if (!elementRef.current) return;

    const checkOverflow = () => {
      if (!elementRef.current) return;

      // Create a clone to measure full height without affecting the current element
      const clone = elementRef.current.cloneNode(true) as T;
      clone.style.position = 'absolute';
      clone.style.visibility = 'hidden';
      clone.style.height = 'auto';
      clone.style.maxHeight = 'none';
      clone.style.setProperty('-webkit-line-clamp', 'unset');
      clone.style.top = '-9999px';
      clone.style.width = elementRef.current.offsetWidth + 'px';

      elementRef.current.parentNode?.appendChild(clone);
      const fullHeight = clone.scrollHeight;
      elementRef.current.parentNode?.removeChild(clone);

      // Calculate what maxLines would be (line-height * maxLines)
      const computedStyle = window.getComputedStyle(elementRef.current);
      const lineHeight = parseFloat(computedStyle.lineHeight) || 20; // fallback to 20px
      const maxLineHeight = lineHeight * maxLines;

      const isOverflow = fullHeight > maxLineHeight;
      setIsOverflowing(isOverflow);
    };

    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkOverflow, 0);

    // Set up ResizeObserver to detect size changes
    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      setTimeout(checkOverflow, 0);
    });
    resizeObserver.observe(elementRef.current);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [text, maxLines]);

  return { elementRef, isOverflowing };
};
